import { useParams } from 'react-router';
import { Button, List, Modal, Typography } from 'antd';
import {
  WarningOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';
import { useQuery } from '@apollo/client/react';
import dayjs from 'dayjs';

import CondensedTable from 'components/CondensedTable/CondensedTable';
import EligibilityTable from '../EligibilityTable';
import useModalControls from 'hooks/useModalControls';
import { FEE_WAIVER_HISTORY } from 'queries/feeWaiver';
import { formatDate } from 'utilities/datesAndTimes';
import { formatDollarsFromCents } from 'utilities/helpers';
import { useMemo } from 'react';
import QueryBoundary from 'components/QueryBoundary/QueryBoundary';

const waiverHistoryColumns = [
  {
    title: 'Waived Date',
    dataIndex: 'date',
    key: 'date',
    render: formatDate,
  },
  {
    title: 'Amount',
    dataIndex: 'amount',
    key: 'amount',
    render: formatDollarsFromCents,
  },
  {
    title: 'Type',
    dataIndex: 'description',
    key: 'description',
  },
  {
    title: 'Related Agent Notes',
    dataIndex: 'relatedNotes',
    key: 'relatedNotes',
    width: '50%',
    render: (relatedNotes) => {
      if (relatedNotes?.length) {
        return (
          <List
            size="small"
            itemLayout="vertical"
            dataSource={relatedNotes}
            renderItem={(relatedNote) => {
              return <List.Item>{relatedNote.note}</List.Item>;
            }}
          />
        );
      }
    },
  },
];

const FeeWaiverHistoryControl = () => {
  const { accountId, customerId } = useParams();
  const feeWaiverHistoryModal = useModalControls();

  const feeWaiverHistoryQuery = useQuery(FEE_WAIVER_HISTORY, {
    variables: {
      accountId,
      customerId,
    },
  });

  const [eligibilityChecks, historyItems] = useMemo(() => {
    const checks = [];
    const items = [];
    const data =
      feeWaiverHistoryQuery.data?.account?.checkEligibility?.eligibility;
    if (data) {
      const reasons = data.reasons || [];
      const notes = data.waiverNotes || [];
      const transactions = data.waiverTransactions || [];

      reasons.forEach((reason) => {
        checks.push({
          name: reason.description,
          pass: reason.pass,
        });
      });

      transactions.forEach((transaction) => {
        const date = dayjs(transaction.date);
        const relatedNotes = notes.filter((note) => {
          return date.isSame(dayjs(note.createdAt), 'day');
        });
        items.push({ ...transaction, date, relatedNotes });
      });
    }
    return [checks, items];
  }, [feeWaiverHistoryQuery.data]);

  const eligible =
    feeWaiverHistoryQuery?.data?.account?.checkEligibility?.eligibility
      ?.eligible;

  return (
    <>
      <Modal
        {...feeWaiverHistoryModal}
        centered
        cancelButtonProps={{ hidden: true }}
        title="Fee Waiver History"
        width={1000}
        styles={{ body: { paddingTop: 'var(--spacing-md)' } }}
      >
        <QueryBoundary
          query={feeWaiverHistoryQuery}
          loadingMessage="Loading fee waiver history"
          errorMessage="Failed to retrieve fee waiver history"
        >
          <div style={{ maxWidth: '600px', marginBottom: 'var(--spacing-xl)' }}>
            <EligibilityTable
              eligible={eligible}
              eligibilityChecks={eligibilityChecks}
            />
          </div>

          <Typography.Paragraph strong>
            Waivers in the last 12 months
          </Typography.Paragraph>
          <CondensedTable columns={waiverHistoryColumns} data={historyItems} />
        </QueryBoundary>
      </Modal>
      <Button
        data-testid="fee-waiver-history-button"
        onClick={feeWaiverHistoryModal.show}
        loading={feeWaiverHistoryQuery.loading}
        icon={
          feeWaiverHistoryQuery.error ? (
            <CloseCircleOutlined style={{ color: 'var(--red-3)' }} />
          ) : eligible ? (
            <CheckCircleOutlined style={{ color: 'var(--green-3)' }} />
          ) : (
            <WarningOutlined style={{ color: 'var(--yellow-3)' }} />
          )
        }
      >
        Fee Waiver Eligibility
      </Button>
    </>
  );
};

export default FeeWaiverHistoryControl;
